import { StatTile } from './StatTile';
import type { MyMonthWeek } from '../types';

interface TimesheetsPendingTileProps {
  /**
   * Count of own complete-but-unsubmitted periods. Weekly: trailing 3-week
   * window. Monthly: completed prior months only (current month excluded).
   */
  count: number | undefined;
  /** Cadence of the payload. Absent ⇒ weekly rendering (unchanged). */
  cadence?: 'WEEKLY' | 'MONTHLY';
  /** In-month week roll-ups — drives the monthly "weeks needing attention". */
  weeks?: MyMonthWeek[];
  /** Most recent completed-but-unsubmitted month, e.g. "June". */
  priorMonthToSubmitLabel?: string;
  loading?: boolean;
  onOpenTimesheets?: () => void;
}

/**
 * My-time mini-tile — replaces the prototype's "Time off balance" tile
 * per the Phase 3b decision ("Change 'time-off balance' to be
 * something related to how many open/submitted timesheets in the
 * selected time period").
 *
 * Cadence-aware:
 *  - **Weekly** — unchanged: count of unsubmitted periods in the trailing
 *    3-week window ("Across this week" / "this + prior N weeks").
 *  - **Monthly** — a month can't be submitted until it ends, so the tile is
 *    *keep-up* mid-month and *submit* once a month closes:
 *      1. A completed prior month is unsubmitted → "Submit {month}".
 *      2. Otherwise → earlier elapsed weeks of the current month that are
 *         below target ("W1, W3 below target" / "On track this month").
 *    The unenforced submit-by deadline is deliberately omitted here — it's
 *    pending its own configurable-deadline + reminders work.
 */
export function TimesheetsPendingTile({
  count,
  cadence,
  weeks,
  priorMonthToSubmitLabel,
  loading,
  onOpenTimesheets,
}: TimesheetsPendingTileProps) {
  if (cadence === 'MONTHLY') {
    return (
      <MonthlyTimesheetsTile
        monthsToSubmit={count ?? 0}
        weeks={weeks}
        priorMonthToSubmitLabel={priorMonthToSubmitLabel}
        loading={loading}
        onOpenTimesheets={onOpenTimesheets}
      />
    );
  }

  const n = count ?? 0;
  const onTrack = n === 0;

  return (
    <StatTile
      label="Timesheets to submit"
      value={n}
      sub={
        onTrack
          ? 'All caught up across recent weeks'
          : `Across ${n === 1 ? 'this' : 'this + prior'} ${n === 1 ? 'week' : `${n - 1} weeks`}`
      }
      valueColor={onTrack ? 'success.main' : 'warning.main'}
      trend={
        onTrack
          ? { value: 1, label: 'on track', tone: 'ok' }
          : { value: -1, label: `${n} pending`, tone: 'warn' }
      }
      onConfigure={onOpenTimesheets}
      configureAriaLabel="Open timesheets"
      loading={loading}
      data-testid="stat-timesheets-pending"
    />
  );
}

interface MonthlyTimesheetsTileProps {
  monthsToSubmit: number;
  weeks: MyMonthWeek[] | undefined;
  priorMonthToSubmitLabel?: string;
  loading?: boolean;
  onOpenTimesheets?: () => void;
}

/**
 * An earlier elapsed week (not the current one) the server reads as short of
 * its in-month target. A week owing nothing — split boundary weeks with no
 * in-month working days, fully holiday/leave weeks, future weeks — is never
 * `under`, which also sidesteps part-time false positives at week granularity.
 */
function incompleteEarlierWeeks(weeks: MyMonthWeek[] | undefined): MyMonthWeek[] {
  return (weeks ?? []).filter((w) => !w.isCurrent && w.state === 'under');
}

function MonthlyTimesheetsTile({
  monthsToSubmit,
  weeks,
  priorMonthToSubmitLabel,
  loading,
  onOpenTimesheets,
}: MonthlyTimesheetsTileProps) {
  // Submit mode — a completed prior month is outstanding. This is the concrete,
  // actionable nudge, so it takes precedence over current-month completeness.
  if (monthsToSubmit > 0) {
    const sub =
      monthsToSubmit === 1 && priorMonthToSubmitLabel
        ? `Submit ${priorMonthToSubmitLabel}`
        : `${monthsToSubmit} months ready to submit`;
    return (
      <StatTile
        label="Timesheets to submit"
        value={monthsToSubmit}
        sub={sub}
        valueColor="warning.main"
        trend={{ value: -1, label: `${monthsToSubmit} to submit`, tone: 'warn' }}
        onConfigure={onOpenTimesheets}
        configureAriaLabel="Open timesheets"
        loading={loading}
        data-testid="stat-timesheets-pending"
      />
    );
  }

  // Completeness mode — keep the current month tidy. Counts only earlier
  // elapsed weeks, so a partly-logged current week is never flagged as behind.
  const incomplete = incompleteEarlierWeeks(weeks);
  const n = incomplete.length;
  const onTrack = n === 0;

  return (
    <StatTile
      label="Weeks to complete"
      value={n}
      sub={
        onTrack
          ? 'On track this month'
          : `${incomplete.map((w) => w.label).join(', ')} below target`
      }
      valueColor={onTrack ? 'success.main' : 'warning.main'}
      trend={
        onTrack
          ? { value: 1, label: 'on track', tone: 'ok' }
          : { value: -1, label: `${n} to finish`, tone: 'warn' }
      }
      onConfigure={onOpenTimesheets}
      configureAriaLabel="Open timesheets"
      loading={loading}
      data-testid="stat-timesheets-pending"
    />
  );
}
